import { useState } from 'react';
import { Button, Card, Empty, Input, Select, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContractsList, type Contract, type ContractStatus } from '../api/contracts';
import { useProjectsList } from '@/features/projects/api/projects';
import { formatUzs } from '@/shared/lib/format';

const STATUS_COLOR: Record<ContractStatus, string> = {
  draft: 'default',
  active: 'green',
  completed: 'blue',
  cancelled: 'red',
  defaulted: 'volcano',
};

const STATUSES: ContractStatus[] = ['draft', 'active', 'completed', 'cancelled', 'defaulted'];

export function ContractsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContractStatus | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading } = useContractsList({
    search: search.trim() || undefined,
    status,
    projectId,
  });
  const { data: projects } = useProjectsList();

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('contracts.title')}
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/contracts/new')}>
          {t('contracts.new')}
        </Button>
      </div>

      <Space wrap>
        <Input.Search
          allowClear
          placeholder={t('contracts.searchPlaceholder')}
          style={{ width: 240 }}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          placeholder={t('contracts.cols.status')}
          style={{ width: 180 }}
          value={status}
          onChange={(v) => setStatus(v)}
          options={STATUSES.map((s) => ({ value: s, label: t(`contracts.status.${s}`) }))}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('contracts.cols.project')}
          style={{ width: 220 }}
          value={projectId}
          onChange={(v) => setProjectId(v)}
          options={(projects?.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
      </Space>

      <Card bodyStyle={{ padding: 0 }}>
        {isLoading ? (
          <Skeleton active style={{ padding: 24 }} />
        ) : (data?.data ?? []).length === 0 ? (
          <Empty style={{ padding: 32 }} description={t('contracts.empty')} />
        ) : (
          <Table<Contract>
            rowKey="id"
            dataSource={data?.data ?? []}
            pagination={false}
            onRow={(record) => ({
              onClick: () => navigate(`/contracts/${record.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: t('contracts.cols.number'),
                dataIndex: 'contractNumber',
                width: 160,
                render: (v: string, c) => (
                  <Link to={`/contracts/${c.id}`} onClick={(e) => e.stopPropagation()}>
                    <Typography.Text code>{v}</Typography.Text>
                  </Link>
                ),
              },
              {
                title: t('contracts.cols.customer'),
                render: (_, c) =>
                  c.customer ? `${c.customer.lastName} ${c.customer.firstName}` : '—',
              },
              {
                title: t('contracts.cols.apartment'),
                render: (_, c) => (c.apartment ? c.apartment.fullCode : '—'),
              },
              {
                title: t('contracts.cols.totalAmount'),
                width: 180,
                render: (_, c) => formatUzs(c.totalAmount),
              },
              {
                title: t('contracts.cols.status'),
                width: 130,
                render: (_, c) => (
                  <Tag color={STATUS_COLOR[c.status]}>{t(`contracts.status.${c.status}`)}</Tag>
                ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}
